import * as React from 'react';
import { useRef, useEffect, useMemo } from 'react';

/** Material UI Packages. */
import MuiAppBar from '@mui/material/AppBar';
import { styled, createTheme, ThemeProvider, Box, Toolbar, CssBaseline } from '@mui/material';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Web Socket Components. */
import { initSocket } from 'socket';
import { handleSocketCreate, handleSocketUpdate, handleSocketDelete, handleSocketRead, handleAssignmentsChanged, handleAutoUpdateNotice } from 'shared/lib/socketHandler';

/** Internal Components. */
import Menubar from './Menubar';
import ComingSoon from 'features/coming-soon';
import Shipments, { isPackingListEvent, packingListChanged } from 'features/shipments';
import DataEntry from 'features/data-entry';
import DutyCalculator from 'features/duty-calculator';
import QuoteArchive from 'features/quote-archive';
import Guideline from 'features/guideline';
import Analytics from 'features/analytics';
import DataExport from 'features/data-export';
import Accounting from 'features/accounting';
import RateManagement from 'features/pricing';
import ShipmentMonitoring from 'features/shipment-monitoring';
import Taskboard from 'features/taskboard';
import ClientSetting from 'features/client-setting';
import Performance from 'features/performance';
import Activity from 'features/activity';
import MyProfile from 'features/my-profile';

/** CSS Style. */
const theme = createTheme({
    palette: {
        primary: {
            light: '#80b2bf',
            main: '#00647e',
            dark: '#003a52',
        },
        secondary: { main: '#c1c1c1' },
        info: {
            main: '#58a000',
        },
        warning: {
            main: '#f48704',
        },
        error: {
            main: '#d92936',
        },
    },
    typography: {
        fontFamily: 'NotoSans',
        fontWeightRegular: 400,
        fontWeightBold: 600,
        fontSize: 13,
    },
    MuiDataGrid: {
        styleOverrides: {
            root: {
                '& .MuiDataGrid-main > div:nth-of-type': {
                    height: 'word-wrap !important',
                },
            },
            row: {
                '&.Mui-selected': {
                    backgroundColor: 'white',
                    '&:hover': {
                        backgroundColor: 'white',
                    },
                },
            },
        },
    },
});

/** Width of the drawer */
const drawerWidth = 180;

/** Styling for the AppBar */
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: '100%',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

export default function Main(props) {
    /** Get props from App.js.  */
    const {
        userRole,
        opdashboard,
        shipments,
        dataentry,
        dutycalculator,
        quotearchive,
        guideline,
        analytics,
        dataexport,
        accounting,
        ratemanagement,
        shipmentmonitoring,
        taskboard,
        clientsetting,
        performance,
        useractivity,
        myprofile,
    } = props;

    /** Reference for detecting scroll position */
    const ref = useRef();

    /** Auth0 authenticator. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Hook to handle data fetching from the server using a socket method. */
    useEffect(() => {
        const email = user.email;
        let cancelled = false;
        let activeSocket = null;

        const onCreate = (data) => handleSocketCreate(data, dispatch);
        const onUpdate = (data) => (isPackingListEvent(data) ? dispatch(packingListChanged(data[4])) : handleSocketUpdate(data, dispatch));
        const onDelete = (data) => handleSocketDelete(data, dispatch);
        const onRead = (data) => handleSocketRead(data, dispatch);
        const onAssignmentsChanged = () => handleAssignmentsChanged(dispatch);
        const onAutoUpdate = (data) => handleAutoUpdateNotice(data, dispatch);

        (async () => {
            try {
                const socket = await initSocket(getAccessTokenSilently, email, dispatch);
                console.log('socket', socket);
                // Effect may have been cleaned up (e.g. route remount) while the socket was initializing.
                if (cancelled) return;

                activeSocket = socket;
                socket.on('create', onCreate);
                socket.on('update', onUpdate);
                socket.on('delete', onDelete);
                socket.on('read', onRead);
                socket.on('assignments_changed', onAssignmentsChanged);
                socket.on('auto_update', onAutoUpdate);
            } catch (e) {
                console.log(e);
            }
        })();

        // Runs synchronously on unmount/remount, unlike the previous cleanup which was
        // returned from inside the async helper and so was never registered with React.
        return () => {
            cancelled = true;
            if (!activeSocket) return;
            activeSocket.off('create', onCreate);
            activeSocket.off('update', onUpdate);
            activeSocket.off('delete', onDelete);
            activeSocket.off('read', onRead);
            activeSocket.off('assignments_changed', onAssignmentsChanged);
            activeSocket.off('auto_update', onAutoUpdate);
        };
    }, []);

    /** Function to render different components based on conditions. */
    const PageComponent = (props) => {
        const {
            opdashboard,
            shipments,
            dataentry,
            dutycalculator,
            quotearchive,
            guideline,
            analytics,
            dataexport,
            accounting,
            ratemanagement,
            shipmentmonitoring,
            taskboard,
            clientsetting,
            performance,
            useractivity,
            myprofile,
        } = props;

        switch (true) {
            case opdashboard:
                return <ComingSoon />;
            case shipments:
                return <Shipments />;
            case dataentry:
                return <DataEntry />;
            case dutycalculator:
                return <DutyCalculator />;
            case quotearchive:
                return <QuoteArchive />;
            case guideline:
                return <Guideline />;
            case analytics:
                return <Analytics />;
            case dataexport:
                return <DataExport />;
            case accounting:
                return <Accounting />;
            case ratemanagement:
                return <RateManagement />;
            case shipmentmonitoring:
                return <ShipmentMonitoring />;
            case taskboard:
                return <Taskboard />;
            case clientsetting:
                return <ClientSetting />;
            case performance:
                return <Performance />;
            case useractivity:
                return <Activity />;
            case myprofile:
                return <MyProfile />;
            default:
                return null;
        }
    };

    /** Memoize the PageComponent to avoid unnecessary re-renders. */
    const PageComponentMemo = useMemo(
        () => PageComponent,
        [
            opdashboard,
            shipments,
            dataentry,
            dutycalculator,
            quotearchive,
            guideline,
            analytics,
            dataexport,
            accounting,
            ratemanagement,
            shipmentmonitoring,
            taskboard,
            clientsetting,
            performance,
            useractivity,
            myprofile,
        ],
    );

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex', bgcolor: '#80b2bf' }}>
                <CssBaseline />
                <AppBar position="absolute" sx={{ boxShadow: 'none' }}>
                    <Menubar userRole={userRole} />
                </AppBar>
                <Box
                    ref={ref}
                    className="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <Toolbar
                        id="scroller"
                        sx={{
                            minHeight: 52,
                        }}
                        variant="dense"
                    />
                    <Box>
                        <PageComponentMemo
                            opdashboard={opdashboard}
                            shipments={shipments}
                            dataentry={dataentry}
                            dutycalculator={dutycalculator}
                            quotearchive={quotearchive}
                            guideline={guideline}
                            analytics={analytics}
                            dataexport={dataexport}
                            accounting={accounting}
                            ratemanagement={ratemanagement}
                            shipmentmonitoring={shipmentmonitoring}
                            taskboard={taskboard}
                            clientsetting={clientsetting}
                            performance={performance}
                            useractivity={useractivity}
                            myprofile={myprofile}
                        />
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
